import type { Tower } from "../entities/Tower.ts";
import type { Enemy } from "../entities/Enemy.ts";
import { pulse, settle, decay, breathe } from "./AnimationUtils.ts";
import { arenaMotionScale } from "../engine/MotionSettings.ts";
import { glowCache } from "./GlowSpriteCache.ts";
import {
  type TowerVisualEffects,
  DEFAULT_VISUAL_EFFECTS,
  drawSkillEffects,
  drawLaserBeam,
  drawUpgradeIndicator,
} from "./TowerSkillEffects.ts";
import { ELEMENT_REGISTRY, ELEMENT_DRAW } from "./ElementRegistry.ts";
import { ENEMY_PALETTE } from "../core/palette.ts";

export type { TowerVisualEffects };
export { DEFAULT_VISUAL_EFFECTS };
export { ELEMENT_DRAW };

/**
 * Renders tower entities (fire/water/lightning) with per-element visuals.
 * Handles upgrade indicators and laser beam effects.
 */
export class TowerRenderer {
  private ctx: CanvasRenderingContext2D;
  private tileSize: number;
  private time = 0;
  effects: TowerVisualEffects = { ...DEFAULT_VISUAL_EFFECTS };

  constructor(ctx: CanvasRenderingContext2D, tileSize: number) {
    this.ctx = ctx;
    this.tileSize = tileSize;
  }

  /**
   * @param jammerHosts — Task-009 (O-10 legibility, D-324): the live jammer
   *  enemies on the field, so a jammed tower can tether back to WHICH enemy is
   *  suppressing it (mirrors the Overseer's `drawAuraLink`). Render-only; an
   *  empty/absent list (every caller before D-324) draws the same jammed-tower
   *  static as before — byte-identical when no jammer is present.
   */
  render(towers: Tower[], time: number, jammerHosts: Enemy[] = []): void {
    const ctx = this.ctx;
    const size = this.tileSize;
    this.time = time;

    for (const tower of towers) {
      // BL-235 — the SOCKET the tower sits on, drawn FIRST (world space, under
      // everything incl. the recoil kick) so towers read as mounted on the
      // board, not floating.
      this.drawSocket(ctx, tower.x, tower.y, size, tower);

      // BL-175 / D-266 — the GOLD pinnacle aura is drawn UNDER the node (a wide,
      // breathing legendary halo in the tower's OWN element hue) so the apex reads
      // even before the crown. Element-true (uses tower.color), NOT gold paint.
      if (tower.isGold()) this.drawGoldUnderAura(tower, size);

      // Pulsing glow ring (outer outline, all tower types)
      this.drawGlowRing(ctx, tower.x, tower.y, size, tower);

      // BL-36 recoil = a directional KICK, not a grow: on fire the body
      // translates back along the barrel, then `settle`s forward (ease-out).
      // The persistent post-upgrade body bump stays (it isn't recoil). Kick is
      // damped under reduced motion (BL-45). recoilT runs 1 → 0 over the 80ms.
      const recoilT = tower.fireCooldownVisual / 80;
      const upgradeScale = tower.level > 1 ? 1.06 : 1;
      // BL-44: arena recoil kick uses the continuous arena-motion factor — it
      // damps gradually under load (the kick magnitude already scales by `m`).
      const m = arenaMotionScale();
      const kicking = recoilT > 0 && m > 0;
      const needsTransform = kicking || upgradeScale !== 1;
      if (needsTransform) {
        ctx.save();
        if (kicking) {
          const ang = this.recoilAngle(tower);
          const kick = size * 0.1 * (1 - settle(1 - recoilT)) * m;
          ctx.translate(-Math.cos(ang) * kick, -Math.sin(ang) * kick);
        }
        if (upgradeScale !== 1) {
          ctx.translate(tower.x, tower.y);
          ctx.scale(upgradeScale, upgradeScale);
          ctx.translate(-tower.x, -tower.y);
        }
      }

      ELEMENT_REGISTRY[tower.element].drawNode(
        ctx,
        tower.x,
        tower.y,
        size,
        tower,
        this.time,
      );

      if (needsTransform) {
        ctx.restore();
      }

      // Muzzle flash (CR-6 successor): a one-shot emissive bloom at the barrel
      // tip on fire, fading on `decay`. Drawn in world space (the shot's exit
      // point), softened under reduced motion.
      if (recoilT > 0) {
        this.drawMuzzleFlash(ctx, tower, size, recoilT, m);
      }

      // BL-175 / D-266 — at GOLD the plain level pips are replaced by the apex
      // CROWN (a richer element-true legendary geometry); below GOLD, the pips.
      if (tower.isGold()) {
        this.drawGoldCrown(tower, size);
      } else if (tower.level > 1) {
        drawUpgradeIndicator(ctx, tower.x, tower.y, size, tower);
      }

      if (
        tower.element === "lightning" &&
        tower.beamTarget &&
        tower.beamActive
      ) {
        drawLaserBeam(ctx, tower, this.time, this.effects);
      }

      // Kinetic rail tracer: a brief bright beam from tower to shot endpoint,
      // fading over the railShotTimer lifetime (set in SniperCombat on fire).
      if (tower.element === "kinetic" && tower.railShotTimer > 0) {
        this.drawRailTracer(ctx, tower);
      }

      // ─── Active Skill Visual Effects ─────────────────────
      drawSkillEffects(
        ctx,
        tower.x,
        tower.y,
        size,
        tower,
        this.time,
        this.effects,
      );

      // S156 / PT-41 — a tower suppressed by a living Jammer (jammedTimer > 0)
      // "statics": a flickering magenta throttle ring so the player SEES which
      // towers run sluggish (the EFFECT-ON-TARGET tell) and can spread out of
      // the field. Render-only; reduced-motion safe (a faint static ring persists
      // at m=0, the flicker/sweep scale by motion).
      if (tower.jammedTimer > 0) {
        this.drawJammedTower(tower, size, m);
        // Task-009 (O-10, D-324) — tether the suppressed tower back to ITS
        // jammer, so "which enemy is doing this" reads at a glance (parity
        // with the Overseer's aura→host tether below).
        this.drawJammerLink(tower, jammerHosts);
      }
    }
  }

  /** S156 / PT-41 — draw the Jammer suppression "static" over a throttled tower:
   *  a sweeping dashed magenta ring + crackling rim ticks. */
  private drawJammedTower(tower: Tower, size: number, m: number): void {
    const ctx = this.ctx;
    const col = ENEMY_PALETTE.jammer!; // central palette (D-224) — no inline hex
    // Fast static flicker — the suppression tell; persists faintly at m=0.
    const flick = 0.5 + Math.abs(Math.sin(this.time * 13 + tower.x * 0.07)) * 0.5;
    ctx.save();
    ctx.strokeStyle = col;
    ctx.lineWidth = 1.4;
    ctx.globalAlpha = 0.22 + flick * 0.28 * m;
    ctx.setLineDash([3, 4]);
    ctx.lineDashOffset = this.time * 30 * m;
    ctx.beginPath();
    ctx.arc(tower.x, tower.y, size * 0.54, 0, Math.PI * 2);
    ctx.stroke();
    ctx.setLineDash([]);
    // Crackle ticks jittering around the rim (the "static" read).
    const ticks = 4;
    ctx.lineWidth = 1;
    for (let i = 0; i < ticks; i++) {
      const a = (i / ticks) * Math.PI * 2 + this.time * 0.8;
      const jit = Math.sin(this.time * 17 + i * 2.3 + tower.x * 0.05);
      const r0 = size * 0.5;
      const r1 = size * (0.62 + 0.1 * jit * (0.4 + 0.6 * m));
      ctx.globalAlpha = 0.12 + (0.3 + 0.4 * Math.abs(jit)) * (0.4 + 0.6 * m);
      ctx.beginPath();
      ctx.moveTo(tower.x + Math.cos(a) * r0, tower.y + Math.sin(a) * r0);
      ctx.lineTo(tower.x + Math.cos(a) * r1, tower.y + Math.sin(a) * r1);
      ctx.stroke();
    }
    ctx.restore();
  }

  /** Task-009 (O-10 legibility, D-324) — the JAM tether: a faint, sweeping
   *  magenta line from a suppressed tower to its (nearest in-range, live)
   *  Jammer, mirroring `EnemyRenderer.drawAuraLink`'s Overseer tether so both
   *  aura families share one "who's doing this to me" idiom, distinguished
   *  only by colour (jammer magenta / overseer violet). Render-only;
   *  reduced-motion safe — a faint static tether still shows the link at
   *  `m = 0`, only the dash-sweep speed and part of the alpha scale by motion.
   *  No-op (and therefore byte-identical) when no live Jammer is in range. */
  private drawJammerLink(tower: Tower, hosts: Enemy[]): void {
    if (hosts.length === 0) return;
    let best: Enemy | null = null;
    let bestD2 = Infinity;
    for (const h of hosts) {
      const radius = h.special?.jamRadius ?? 0;
      const dx = h.x - tower.x;
      const dy = h.y - tower.y;
      const d2 = dx * dx + dy * dy;
      if (d2 <= radius * radius && d2 < bestD2) {
        bestD2 = d2;
        best = h;
      }
    }
    if (!best) return;
    const ctx = this.ctx;
    const m = arenaMotionScale();
    ctx.save();
    ctx.strokeStyle = ENEMY_PALETTE.jammer!;
    ctx.lineWidth = 1;
    ctx.globalAlpha = 0.16 + breathe(this.time, 1.2) * 0.12 * (0.4 + 0.6 * m);
    ctx.setLineDash([2, 4]);
    ctx.lineDashOffset = -this.time * 12 * m;
    ctx.beginPath();
    ctx.moveTo(tower.x, tower.y);
    ctx.lineTo(best.x, best.y);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.restore();
  }

  /** Direction the barrel points (radians, 0 = +x). Kinetic eases a real
   *  `barrelAngle`; other elements aim at the current target; idle towers
   *  default to pointing up. The recoil kicks the body the opposite way. */
  private recoilAngle(tower: Tower): number {
    if (tower.element === "kinetic") return tower.barrelAngle;
    const t = tower.target;
    if (t && t.active) return Math.atan2(t.y - tower.y, t.x - tower.x);
    return -Math.PI / 2;
  }

  /** One-shot muzzle bloom at the barrel tip on fire — a colored flash + a
   *  white-hot core, additive, fading on `decay` (bright for ~1–2 frames). */
  private drawMuzzleFlash(
    ctx: CanvasRenderingContext2D,
    tower: Tower,
    size: number,
    recoilT: number,
    m: number,
  ): void {
    const a = (m > 0 ? 1 : 0.5) * (1 - decay(1 - recoilT));
    if (a <= 0.01) return;
    const ang = this.recoilAngle(tower);
    const tipDist = size * 0.42;
    const mx = tower.x + Math.cos(ang) * tipDist;
    const my = tower.y + Math.sin(ang) * tipDist;
    const prevOp = ctx.globalCompositeOperation;
    ctx.globalCompositeOperation = "lighter";
    glowCache.draw(ctx, tower.colorLight, size * 0.22, mx, my, a * 0.9);
    glowCache.draw(ctx, "#FFFFFF", size * 0.1, mx, my, a);
    ctx.globalCompositeOperation = prevOp;
  }

  /** Kinetic hitscan tracer: bright core + colored glow line, alpha fades as
   *  railShotTimer decays from 160ms → 0. Purely cosmetic. */
  private drawRailTracer(ctx: CanvasRenderingContext2D, tower: Tower): void {
    const t = Math.max(0, Math.min(1, tower.railShotTimer / 160));
    const ex = tower.railShotX;
    const ey = tower.railShotY;

    ctx.save();
    // Outer colored glow
    ctx.globalAlpha = t * 0.5;
    ctx.strokeStyle = tower.color;
    ctx.lineWidth = 5;
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(tower.x, tower.y);
    ctx.lineTo(ex, ey);
    ctx.stroke();

    // Bright inner core
    ctx.globalAlpha = t;
    ctx.strokeStyle = "#FFFFFF";
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(tower.x, tower.y);
    ctx.lineTo(ex, ey);
    ctx.stroke();

    // Impact spark at the endpoint
    ctx.fillStyle = tower.colorLight;
    ctx.globalAlpha = t;
    ctx.beginPath();
    ctx.arc(ex, ey, 3 + (1 - t) * 5, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  /** BL-235 — the base SOCKET a tower is mounted on: a grounding drop-shadow
   *  hex + a dark base plate with a thin element-tinted rim + a faint element
   *  contact-glow. Drawn UNDER the node so towers don't float over the board.
   *  Static (no animation) ⇒ calm-pass + reduced-motion safe; dark fills are
   *  rgba (no governed tokens), the rim/glow use the tower's own element hue. */
  private drawSocket(
    ctx: CanvasRenderingContext2D,
    cx: number,
    cy: number,
    size: number,
    tower: Tower,
  ): void {
    const r = size * 0.46;
    const hex = (rad: number, oy: number): void => {
      ctx.beginPath();
      for (let i = 0; i < 6; i++) {
        const a = Math.PI / 6 + (i * Math.PI) / 3;
        const x = cx + Math.cos(a) * rad;
        const y = cy + oy + Math.sin(a) * rad;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.closePath();
    };

    // 1) Drop shadow — a larger dark hex nudged down, grounds the tower.
    ctx.save();
    ctx.globalAlpha = 0.32;
    ctx.fillStyle = "rgba(0,0,0,1)";
    hex(r * 1.04, size * 0.1);
    ctx.fill();
    ctx.restore();

    // 2) Base plate — a dark hex + a thin element-tinted rim.
    ctx.save();
    hex(r, 0);
    ctx.globalAlpha = 0.55;
    ctx.fillStyle = "rgba(8,10,18,1)";
    ctx.fill();
    ctx.globalAlpha = 0.3;
    ctx.strokeStyle = tower.color;
    ctx.lineWidth = 1.2;
    ctx.stroke();
    ctx.restore();

    // 3) Faint element contact-glow seated at the base.
    glowCache.draw(ctx, tower.color, r * 0.7, cx, cy + size * 0.04, 0.1);
  }

  private drawGlowRing(
    ctx: CanvasRenderingContext2D,
    cx: number,
    cy: number,
    size: number,
    tower: Tower,
  ): void {
    // F-11: brighter, slightly wider glow at higher upgrade levels.
    const levelBoost = (tower.level - 1) * 0.08;
    const glowAlpha = pulse(this.time, 1.2, 0.15, 0.4) + levelBoost;
    const r = size * (0.42 + (tower.level - 1) * 0.02);
    glowCache.draw(ctx, tower.color, r, cx, cy, Math.min(1, glowAlpha));
    ctx.strokeStyle = tower.color;
    ctx.lineWidth = 1;
    ctx.globalAlpha = glowAlpha;
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.stroke();
    ctx.globalAlpha = 1;
  }

  /** BL-175 / D-266 (S170) — the GOLD pinnacle UNDER-aura: a wide, breathing
   *  legendary halo + a slow scanline sweep (the `RARITY.legendary` apex
   *  vocabulary) rendered in the tower's OWN element hue — the "legendary
   *  material", NOT gold paint. A signature-pulse flare punches it brighter when
   *  the element's GOLD mechanic fires (`goldPulseT`), and an outward command-aura
   *  ring shows when this tower is empowered by a GOLD ORACLE (`commandAuraLit`).
   *  All motion damps with `arenaMotionScale` (reduced-motion / load safe). */
  private drawGoldUnderAura(tower: Tower, size: number): void {
    const ctx = this.ctx;
    const m = arenaMotionScale();
    const breath = 0.5 + 0.5 * Math.sin(this.time * 1.6 + tower.x * 0.03);
    const pulseT = tower.goldPulseT > 0 ? tower.goldPulseT / 320 : 0;

    // Wide breathing halo in the element hue (the apex bloom seed).
    const haloR = size * (0.72 + 0.06 * breath * m) + size * 0.5 * pulseT;
    glowCache.draw(
      ctx,
      tower.colorLight,
      haloR,
      tower.x,
      tower.y,
      Math.min(1, 0.32 + 0.18 * breath * m + 0.5 * pulseT),
    );

    // A slow legendary scanline sweep across the halo (the §3 apex treatment),
    // tinted to the element so the read stays element-true.
    ctx.save();
    const sweep = ((this.time * 0.4 * m) % 1) * 2 - 1; // -1 → 1
    const sy = tower.y + sweep * size * 0.6;
    ctx.globalAlpha = (0.12 + 0.1 * breath) * m;
    ctx.strokeStyle = tower.colorLight;
    ctx.lineWidth = 1.4;
    ctx.beginPath();
    ctx.moveTo(tower.x - size * 0.5, sy);
    ctx.lineTo(tower.x + size * 0.5, sy);
    ctx.stroke();
    ctx.restore();

    // ORACLE empowerment tell: a bright outward ring on a buffed tower.
    if (tower.commandAuraLit) {
      ctx.save();
      ctx.globalAlpha = 0.3 + 0.2 * breath * m;
      ctx.strokeStyle = tower.colorLight;
      ctx.lineWidth = 1.2;
      ctx.setLineDash([2, 3]);
      ctx.lineDashOffset = -this.time * 22 * m;
      ctx.beginPath();
      ctx.arc(tower.x, tower.y, size * 0.62, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
    }
  }

  /** BL-175 / D-266 (S170) — the GOLD pinnacle CROWN drawn OVER the node: a ring of
   *  sharp laurel points orbiting the apex, in the tower's OWN element hue elevated
   *  to white-hot legendary tips. Replaces the plain level pips at GOLD — a coveted
   *  "must-have" silhouette read at a glance. Motion-gated; at m=0 a static crown
   *  still reads (accessible). */
  private drawGoldCrown(tower: Tower, size: number): void {
    const ctx = this.ctx;
    const m = arenaMotionScale();
    const cx = tower.x;
    const cy = tower.y;
    const spin = this.time * 0.5 * m;
    const points = 8;
    const innerR = size * 0.46;
    const outerR = size * (0.6 + 0.04 * Math.sin(this.time * 3) * m);

    ctx.save();
    ctx.lineWidth = 1.4;
    ctx.strokeStyle = tower.color;
    ctx.fillStyle = tower.colorLight;
    for (let i = 0; i < points; i++) {
      const a = (i / points) * Math.PI * 2 + spin;
      const bx = cx + Math.cos(a) * innerR;
      const by = cy + Math.sin(a) * innerR;
      const tx = cx + Math.cos(a) * outerR;
      const ty = cy + Math.sin(a) * outerR;
      // laurel spike: a thin triangle from the rim outward
      const perp = a + Math.PI / 2;
      const w = size * 0.05;
      ctx.beginPath();
      ctx.moveTo(bx + Math.cos(perp) * w, by + Math.sin(perp) * w);
      ctx.lineTo(tx, ty);
      ctx.lineTo(bx - Math.cos(perp) * w, by - Math.sin(perp) * w);
      ctx.closePath();
      ctx.globalAlpha = 0.85;
      ctx.fill();
      ctx.globalAlpha = 1;
      ctx.stroke();
      // Hot tip jewel (the legendary apex flare) — the element's own light hue
      // (palette-true; the WebGL bloom blows it to white-hot on the apex pass).
      ctx.beginPath();
      ctx.arc(tx, ty, size * 0.022, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  }
}
